import { useAuth } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { AppConfig } from "@/config/app.config";
import { logger } from "@/util/logger";

/**
 * Geo API response schema
 * CloudFront determines user location and returns country/region
 * Note: Fields are nullable if CloudFront fails to determine location
 */
const GeoResponseSchema = z.object({
  country: z.string().nullable().optional(),
  countryRegion: z.string().nullable().optional(),
});

export interface GeoLocation {
  country: string | null;
  countryRegion: string | null;
  isUSUser: boolean;
  isNYUser: boolean;
}

const fetchGeoLocation = async (getToken: () => Promise<string | null>): Promise<GeoLocation> => {
  try {
    const token = await getToken();

    const response = await fetch(AppConfig.api.geoUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error(`Geo API request failed: ${response.status}`);
    }

    const data = await response.json();

    // Handle empty JSON hash when CloudFront fails
    if (!data || Object.keys(data).length === 0) {
      logger.warn("Geo API returned empty response");
      return {
        country: null,
        countryRegion: null,
        isUSUser: false,
        isNYUser: false,
      };
    }

    const parsed = GeoResponseSchema.safeParse(data);

    if (!parsed.success) {
      logger.warn("Failed to parse geo response", { error: parsed.error });
      return {
        country: null,
        countryRegion: null,
        isUSUser: false,
        isNYUser: false,
      };
    }

    const country = parsed.data.country ?? null;
    const countryRegion = parsed.data.countryRegion ?? null;
    const isUSUser = country === "US";
    const isNYUser = isUSUser && countryRegion === "NY";

    return {
      country,
      countryRegion,
      isUSUser,
      isNYUser,
    };
  } catch (error) {
    logger.error("Error fetching geo location", { error });
    // Return safe defaults - don't block users if geo detection fails
    return {
      country: null,
      countryRegion: null,
      isUSUser: false,
      isNYUser: false,
    };
  }
};

/**
 * Hook to get user's geo location via CloudFront
 * Used for compliance checks (e.g., NY crypto restrictions)
 *
 * CloudFront determines user location from IP and provides:
 * - country: Country code (e.g., "US")
 * - countryRegion: State/region code (e.g., "NY")
 *
 * Note: Fields are nullable if CloudFront fails to determine location
 */
export const useGeoLocation = () => {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["geo-location"],
    queryFn: () => fetchGeoLocation(getToken),
    staleTime: Number.POSITIVE_INFINITY, // Geo location doesn't change during session
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1, // Only retry once - if it fails, use fallback
  });
};
